import React, { useState } from "react";
import {
  Alert,
  Box,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSessionQuery } from "../../../auth/hooks";
import { clientManagementApi } from "../api/clientManagement";
import { qk } from "../../../../lib/queryKeys";
import {
  DefaultsTab,
  EmailTemplatesTab,
  NotificationRecipientsTab,
  QuestionsTab,
} from "../components";
import { emptyManagementState, type ClientManagementState } from "../types";

const PLATFORM_TABS = [
  { value: "defaults", label: "Defaults" },
  { value: "email-templates", label: "Email Templates" },
  { value: "questions", label: "Questions" },
  { value: "notification-recipients", label: "Notification Recipients" },
];


export const PlatformSettings: React.FC = () => {
  const queryClient = useQueryClient();
  const { data: session } = useSessionQuery();
  const isCentralAdmin = session?.user?.is_central_admin ?? false;

  const [activeTab, setActiveTab] = useState("defaults");
  const [managementState, setManagementState] =
    useState<ClientManagementState>(emptyManagementState);
  const [selectedEmailTemplateKey, setSelectedEmailTemplateKey] = useState("pre_registration_interest");

  const emailTemplateOptions = [
    { value: "pre_registration_interest", label: "Pre-registration Interest" },
    { value: "rejected_user", label: "Rejected User" },
  ];

  const handleManagementChange = (
    field: keyof ClientManagementState,
    value: string | boolean
  ) => {
    setManagementState((prev) => ({ ...prev, [field]: value }));
  };

  // ── Queries ────────────────────────────────────────────────────────────────

  const defaultJobRolesQuery = useQuery({
    queryKey: qk.defaultJobRoles(),
    queryFn: () => clientManagementApi.listDefaultJobRoles(),
    enabled: isCentralAdmin,
    staleTime: 5 * 60 * 1000,
  });

  const defaultDepartmentsQuery = useQuery({
    queryKey: qk.defaultDepartments(),
    queryFn: () => clientManagementApi.listDefaultDepartments(),
    enabled: isCentralAdmin,
    staleTime: 5 * 60 * 1000,
  });

  const emailTemplateQuery = useQuery({
    queryKey: qk.emailTemplate(selectedEmailTemplateKey),
    queryFn: () => clientManagementApi.getEmailTemplate(selectedEmailTemplateKey),
    enabled: isCentralAdmin,
    staleTime: 2 * 60 * 1000,
  });

  // ── Mutations ──────────────────────────────────────────────────────────────

  const createDefaultJobRoleMutation = useMutation({
    mutationFn: (payload: { name: string; is_active: boolean }) =>
      clientManagementApi.createDefaultJobRole(payload),
    onSuccess: () => {
      setManagementState((prev) => ({ ...prev, jobRoleName: "", jobRoleActive: true }));
      queryClient.invalidateQueries({ queryKey: qk.defaultJobRoles() });
    },
  });

  const updateDefaultJobRoleMutation = useMutation({
    mutationFn: ({ jobRoleId, payload }: { jobRoleId: string; payload: { name?: string; is_active?: boolean } }) =>
      clientManagementApi.updateDefaultJobRole(jobRoleId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.defaultJobRoles() });
    },
  });

  const deleteDefaultJobRoleMutation = useMutation({
    mutationFn: (jobRoleId: string) => clientManagementApi.deleteDefaultJobRole(jobRoleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.defaultJobRoles() });
    },
  });

  const createDefaultDepartmentMutation = useMutation({
    mutationFn: (payload: { name: string; is_active: boolean }) =>
      clientManagementApi.createDefaultDepartment(payload),
    onSuccess: () => {
      setManagementState((prev) => ({ ...prev, departmentName: "", departmentActive: true }));
      queryClient.invalidateQueries({ queryKey: qk.defaultDepartments() });
    },
  });

  const updateDefaultDepartmentMutation = useMutation({
    mutationFn: ({ departmentId, payload }: { departmentId: string; payload: { name?: string; is_active?: boolean } }) =>
      clientManagementApi.updateDefaultDepartment(departmentId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.defaultDepartments() });
    },
  });

  const deleteDefaultDepartmentMutation = useMutation({
    mutationFn: (departmentId: string) => clientManagementApi.deleteDefaultDepartment(departmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.defaultDepartments() });
    },
  });

  const updateEmailTemplateMutation = useMutation({
    mutationFn: (payload: { name?: string; subject: string; html_body: string; text_body: string; is_active?: boolean }) =>
      clientManagementApi.updateEmailTemplate(selectedEmailTemplateKey, payload),
    onSuccess: (response) => {
      queryClient.setQueryData(qk.emailTemplate(selectedEmailTemplateKey), response);
      queryClient.invalidateQueries({ queryKey: qk.emailTemplate(selectedEmailTemplateKey) });
    },
  });

  // ── Render ─────────────────────────────────────────────────────────────────

  if (!isCentralAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Central admin access required.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 2.5 } }}>
      <Box mb={2}>
        <Typography variant="h6" fontWeight={700}>
          Platform Settings
        </Typography>
      </Box>

      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          borderBottom: "1px solid",
          borderColor: "divider",
          mb: 3,
          minHeight: 40,
          "& .MuiTab-root": { minHeight: 40, py: 0 },
        }}
      >
        {PLATFORM_TABS.map((tab) => (
          <Tab
            key={tab.value}
            value={tab.value}
            label={tab.label}
            sx={{ textTransform: "none", fontWeight: 500, fontSize: 13 }}
          />
        ))}
      </Tabs>

      {activeTab === "defaults" && (
        <DefaultsTab
          managementState={managementState}
          onChange={handleManagementChange}
          defaultDepartmentsQuery={defaultDepartmentsQuery}
          createDefaultDepartmentMutation={createDefaultDepartmentMutation}
          updateDefaultDepartmentMutation={updateDefaultDepartmentMutation}
          deleteDefaultDepartmentMutation={deleteDefaultDepartmentMutation}
          defaultJobRolesQuery={defaultJobRolesQuery}
          createDefaultJobRoleMutation={createDefaultJobRoleMutation}
          updateDefaultJobRoleMutation={updateDefaultJobRoleMutation}
          deleteDefaultJobRoleMutation={deleteDefaultJobRoleMutation}
        />
      )}

      {activeTab === "email-templates" && (
        <EmailTemplatesTab
          isCentralAdmin={isCentralAdmin}
          selectedTemplateKey={selectedEmailTemplateKey}
          onTemplateChange={setSelectedEmailTemplateKey}
          templateOptions={emailTemplateOptions}
          templateQuery={emailTemplateQuery}
          updateTemplateMutation={updateEmailTemplateMutation}
        />
      )}

      {activeTab === "questions" && (
        <QuestionsTab
          effectiveClientId={null}
          isCentralAdmin={isCentralAdmin}
        />
      )}

      {activeTab === "notification-recipients" && <NotificationRecipientsTab />}
    </Box>
  );
};
